import { SetTheStateForMultipleAggregates } from 'src/models/status/SetStatusCommand';
import { StatusEnum } from 'src/models/status/enums';

import { aggregateIdWithUnsetSourceAndTargetContainerId } from 'src/util/aggregate/AggregateUtilityFunctions';
import { CommandBus } from 'src/util/command/CommandBus';

const DEBOUNCE_MS = 400;
const GUARD_TIMEOUT_MS = 10_000;

export interface StatusWriteContext {
	commandBus: CommandBus | undefined;
	processId: string;
}

export interface SetStatusPayload {
	aggregateIds: string[];
	commandBus: CommandBus | undefined;
	processId: string;
}

interface PendingWrite {
	aggregateId: string;
	context: StatusWriteContext;
	state: StatusEnum;
}

interface StatusGuard {
	expiry: ReturnType<typeof setTimeout>;
	target: StatusEnum;
}

const pendingWrites = new Map<string, PendingWrite>();
const guards = new Map<string, StatusGuard>();
let debounceTimer: ReturnType<typeof setTimeout> | null = null;

function normalizeKey(aggregateId: string): string {
	return aggregateIdWithUnsetSourceAndTargetContainerId(aggregateId);
}

function clearGuard(key: string): void {
	const guard = guards.get(key);
	if (guard !== undefined) {
		clearTimeout(guard.expiry);
		guards.delete(key);
	}
}

function armGuard(key: string, target: StatusEnum): void {
	clearGuard(key);
	guards.set(key, {
		expiry: setTimeout(() => {
			guards.delete(key);
		}, GUARD_TIMEOUT_MS),
		target,
	});
}

function dispatchPendingWrites(): void {
	const grouped = new Map<string, { context: StatusWriteContext; ids: string[]; state: StatusEnum }>();

	pendingWrites.forEach((pending) => {
		const groupKey = `${pending.state}::${pending.context.processId}`;
		const group = grouped.get(groupKey);
		if (group === undefined) {
			grouped.set(groupKey, { context: pending.context, ids: [pending.aggregateId], state: pending.state });
		} else {
			group.context = pending.context;
			group.ids.push(pending.aggregateId);
		}
	});
	pendingWrites.clear();

	grouped.forEach((group) => {
		void group.context.commandBus?.sendCommand(
			new SetTheStateForMultipleAggregates(group.ids, group.context.processId, group.state)
		);
	});
}

export function queueStatusWrite(aggregateIds: string[], state: StatusEnum, context: StatusWriteContext): void {
	if (aggregateIds.length === 0) {
		return;
	}

	aggregateIds.forEach((aggregateId) => {
		const key = normalizeKey(aggregateId);
		pendingWrites.set(key, { aggregateId, context, state });
		armGuard(key, state);
	});

	if (debounceTimer !== null) {
		clearTimeout(debounceTimer);
	}
	debounceTimer = setTimeout(() => {
		debounceTimer = null;
		dispatchPendingWrites();
	}, DEBOUNCE_MS);
}

export function flushAllPendingStatusWrites(): void {
	if (debounceTimer !== null) {
		clearTimeout(debounceTimer);
		debounceTimer = null;
	}
	dispatchPendingWrites();
}

export function resetStatusWritesOnLogout(): void {
	flushAllPendingStatusWrites();
	guards.forEach((guard) => clearTimeout(guard.expiry));
	guards.clear();
}

export function reconcileIncomingStatus(aggregateId: string, incoming: StatusEnum): StatusEnum {
	const key = normalizeKey(aggregateId);
	const guard = guards.get(key);
	if (guard === undefined) {
		return incoming;
	}
	return guard.target;
}

export function __resetStatusWritesForTest(): void {
	if (debounceTimer !== null) {
		clearTimeout(debounceTimer);
		debounceTimer = null;
	}
	guards.forEach((guard) => clearTimeout(guard.expiry));
	guards.clear();
	pendingWrites.clear();
}
